import { Col, Row, Switch } from "antd";
import cuid from "cuid";
import { get, isEmpty, set } from "lodash";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  MdOutlineFreeCancellation,
  MdOutlineFrontHand,
  MdOutlinePinDrop,
  MdOutlineShareLocation,
} from "react-icons/md";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { SUBSCRIPTION_FORM_KEYS } from "utils/constants";

const TerminationRequest = ({
  formProps,
  values,
  initialValues,
  setPercent,
  errors,
  isFieldRequired,
  setDisabled,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const contractMoreThanOneYear = get(
    initialValues,
    "project.fields.contractMoreThanOneYear",
    false,
  );

  const terminationRequestDisabledFields = {
    [false]: {
      contractNumber: true,
      brokerageMandate: {
        currentCompany: true,
        lastName: true,
        firstName: true,
      },
    },
    [true]: {
      contractNumber: false,
      brokerageMandate: {
        currentCompany: false,
        lastName: false,
        firstName: false,
      },
    },
  };

  const applyTerminationRequestDisabledState = (nextDisabled) => {
    flushSync(() => {
      setDisabled((prev) => {
        const next = {
          ...prev,
          project: {
            ...prev.project,
            terminationRequest: nextDisabled,
          },
        };
        setPercent(initialValues, next);
        return next;
      });
    });
  };

  const handleContractMoreThan12MonthsChange = (checked) => {
    set(initialValues, "project.fields.contractMoreThanOneYear", checked);
    if (checked) {
      set(initialValues, "project.terminationRequest", {
        id: cuid(),
        brokerageMandate: { id: cuid() },
      });
      set(
        initialValues,
        "project.terminationRequest.brokerageMandate.lastName",
        get(initialValues, "project.contact.user.lastname"),
      );
      set(
        initialValues,
        "project.terminationRequest.brokerageMandate.firstName",
        get(initialValues, "project.contact.user.firstname"),
      );
    } else {
      set(initialValues, "project.fields.selfOccupation", undefined);
      set(initialValues, "project.terminationRequest", null);
    }

    applyTerminationRequestDisabledState(
      get(terminationRequestDisabledFields, checked),
    );
  };

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST}-section`}
    >
      <Form
        type="vertical"
        title={
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.TITLE
        }
        subtitle={
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.SUBTITLE
        }
      >
        <Row gutter={[16, 16]}>
          {/* CA-03: S'agit-il d'une reprise à la concurrence d'un contrat de plus de 12 mois ? */}
          <Col lg={24} xs={24}>
            <div className="d-flex justify--between align--center">
              <span>
                {SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST
                  .REPRISE_CONTRACT_12_MOIS ||
                  "S'agit-il d'une reprise à la concurrence d'un contrat de plus de 12 mois ?"}
              </span>
              <Switch
                checked={contractMoreThanOneYear}
                onChange={handleContractMoreThan12MonthsChange}
              />
            </div>
          </Col>

          {/* CA-05: Souhaitez-vous confier à la Mutuelle du Soleil la résiliation... (shown when CA-03 = Oui) */}
          {contractMoreThanOneYear && (
            <Col lg={24} xs={24}>
              <div className="d-flex justify--between align--center">
                <span>
                  {SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST
                    .WANTS_MUTUELLE_SOLEIL_RESILIATION ||
                    "Souhaitez-vous confier à la Mutuelle du Soleil la résiliation de votre contrat de complémentaire santé actuel ?"}
                </span>
                <Switch
                  defaultChecked={
                    get(initialValues, "project.fields.selfOccupation") ===
                    false
                  }
                  onChange={(e) => {
                    set(initialValues, "project.fields.selfOccupation", !e);
                  }}
                />
              </div>
            </Col>
          )}

          {/* CA-06: Champs affichés dès que CA-03 = Oui (quelle que soit la réponse à CA-05) */}
          {contractMoreThanOneYear && (
            <>
              {/* CA-07: N° de contrat - obligatoire */}
              <Col lg={24} xs={24}>
                <Input
                  placeholder={SUBSCRIPTION.FORM.CONTRACT_NUMBER.PLACEHOLDER}
                  label={SUBSCRIPTION.FORM.CONTRACT_NUMBER.LABEL}
                  icon={<MdOutlineFrontHand size={16} />}
                  defaultValue={get(
                    initialValues,
                    "project.terminationRequest.contractNumber",
                  )}
                  errors={errors["project.terminationRequest.contractNumber"]}
                  touched={
                    !isEmpty(
                      errors["project.terminationRequest.contractNumber"],
                    )
                  }
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      "project.terminationRequest.contractNumber",
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired(
                    "project.terminationRequest.contractNumber",
                  )}
                />
              </Col>

              {/* CA-08: Nom de votre assureur actuel - texte libre, obligatoire */}
              <Col lg={24} xs={24}>
                <Input
                  placeholder={SUBSCRIPTION.FORM.CURRENT_COMPANY.LABEL}
                  label={SUBSCRIPTION.FORM.CURRENT_COMPANY.LABEL}
                  icon={<MdOutlineShareLocation size={16} />}
                  defaultValue={get(
                    initialValues,
                    "project.terminationRequest.brokerageMandate.currentCompany",
                  )}
                  errors={
                    errors[
                      "project.terminationRequest.brokerageMandate.currentCompany"
                    ]
                  }
                  touched={
                    !isEmpty(
                      errors[
                        "project.terminationRequest.brokerageMandate.currentCompany"
                      ],
                    )
                  }
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      "project.terminationRequest.brokerageMandate.currentCompany",
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired(
                    "project.terminationRequest.brokerageMandate.currentCompany",
                  )}
                />
              </Col>

              {/* CA-09: Adresse de l'assureur actuel - facultatif */}
              <Col lg={24} xs={24}>
                <Input
                  optional={SUBSCRIPTION.FORM.OPTIONAL}
                  placeholder={
                    SUBSCRIPTION.FORM.CURRENT_COMPANY_ADDRESS.PLACEHOLDER
                  }
                  label={SUBSCRIPTION.FORM.CURRENT_COMPANY_ADDRESS.LABEL}
                  icon={<MdOutlinePinDrop size={16} />}
                  defaultValue={get(
                    initialValues,
                    "project.terminationRequest.address",
                  )}
                  errors={errors["project.terminationRequest.address"]}
                  touched={
                    !isEmpty(errors["project.terminationRequest.address"])
                  }
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      "project.terminationRequest.address",
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={false}
                />
              </Col>

              {/* CA-10: Nom - pré-rempli assuré principal, modifiable, obligatoire */}
              <Col lg={24} xs={24}>
                <Input
                  placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                  label={SUBSCRIPTION.FORM.LAST_NAME.LABEL}
                  icon={<MdOutlineFreeCancellation size={16} />}
                  defaultValue={get(
                    initialValues,
                    "project.terminationRequest.brokerageMandate.lastName",
                  )}
                  errors={
                    errors[
                      "project.terminationRequest.brokerageMandate.lastName"
                    ]
                  }
                  touched={
                    !isEmpty(
                      errors[
                        "project.terminationRequest.brokerageMandate.lastName"
                      ],
                    )
                  }
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      "project.terminationRequest.brokerageMandate.lastName",
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired(
                    "project.terminationRequest.brokerageMandate.lastName",
                  )}
                />
              </Col>

              {/* CA-11: Prénom - pré-rempli assuré principal, modifiable, obligatoire */}
              <Col lg={24} xs={24}>
                <Input
                  placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                  label={SUBSCRIPTION.FORM.FIRST_NAME.LABEL}
                  icon={<MdOutlineShareLocation size={16} />}
                  defaultValue={get(
                    initialValues,
                    "project.terminationRequest.brokerageMandate.firstName",
                  )}
                  errors={
                    errors[
                      "project.terminationRequest.brokerageMandate.firstName"
                    ]
                  }
                  touched={
                    !isEmpty(
                      errors[
                        "project.terminationRequest.brokerageMandate.firstName"
                      ],
                    )
                  }
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      "project.terminationRequest.brokerageMandate.firstName",
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired(
                    "project.terminationRequest.brokerageMandate.firstName",
                  )}
                />
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default TerminationRequest;
